/**
 * 💝 Donate — Wave 5 · Value-tiered donor cards
 * ════════════════════════════════════════════════════════════════════════
 *
 * Public page at /donate. Three preset gift tiers ($50 · $150 · $500)
 * that redirect to prewired Stripe Payment Links via env vars:
 *   REACT_APP_STRIPE_DONATE_50 / _150 / _500
 *
 * Confetti fires on card click AND on ?thankyou=1 return (Stripe
 * Payment Link redirect back).
 *
 * The named export `<DonorTiersStrip />` is a compact 3-card band
 * embedded on the /impact page.
 */
import axios from "axios";
import { useEffect } from "react";
import confetti from "canvas-confetti";
import { Heart, ArrowRight, Sparkles, ShieldCheck, Home as HomeIcon, Briefcase } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentMeta from "../hooks/useDocumentMeta";


const TIERS = [
  {
    id:          "spark",
    amount:      50,
    label:       "The Spark",
    envKey:      "REACT_APP_STRIPE_DONATE_50",
    tagline:     "One night of stability.",
    detail:      "Covers a bed, a locking cabinet, and a hot meal for a resident's first night off the street. It's the first breath of a 730-day path.",
    icon:        HomeIcon,
    accent:      "#C9A227",
    accent_bg:   "#FFFBEC",
  },
  {
    id:          "anchor",
    amount:      150,
    label:       "The Anchor",
    envKey:      "REACT_APP_STRIPE_DONATE_150",
    tagline:     "One full week of case management.",
    detail:      "Underwrites a full week of licensed case-management, medical baseline, and vital-document recovery — the Cat-1 stabilization sprint.",
    icon:        ShieldCheck,
    accent:      "#1B4332",
    accent_bg:   "#ECFDF5",
    featured:    true,
  },
  {
    id:          "career",
    amount:      500,
    label:       "The Career",
    envKey:      "REACT_APP_STRIPE_DONATE_500",
    tagline:     "One industry certification.",
    detail:      "Funds a full industry certification — OSHA-30, ServSafe, CDL prep, or an equivalent path — that unlocks a $40K+ starting wage.",
    icon:        Briefcase,
    accent:      "#7B3F00",
    accent_bg:   "#FFF7E6",
  },
];


// Confetti burst — big and celebratory for tap; softer for on-return
function fireConfetti({ big = false } = {}) {
  const count = big ? 200 : 90;
  const defaults = { spread: 70, ticks: 200, gravity: 1.1, decay: 0.94, startVelocity: 45, colors: ["#F4D88A", "#C9A227", "#1B4332", "#0A0612", "#FFFFFF"] };
  const fire = (particleRatio, opts) => {
    confetti({ ...defaults, ...opts, particleCount: Math.floor(count * particleRatio) });
  };
  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2,  { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.9 });
  fire(0.1,  { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  fire(0.1,  { spread: 120, startVelocity: 45 });
}


function tierUrl(tier) {
  const url = (process.env[tier.envKey] || "").trim();
  if (url) return url;
  return null;
}


export default function Donate() {
  useDocumentMeta({
    title:       "Donate | The Exchange Alliance",
    description: "Three preset gift levels that map cleanly to a resident's 24-month path. Every dollar routes into program cost — housing, case management, and industry certification.",
    canonical:   "https://theexchangealliance.com/donate",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const location = useLocation();
  const navigate = useNavigate();

  // Thank-you return from Stripe fires a celebratory confetti burst
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("thankyou") === "1" || p.get("success") === "1") {
      setTimeout(() => fireConfetti({ big: true }), 300);
    }
    if (p.get("session_id")) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/site/donate/status/${p.get("session_id")}`).catch(() => {});
    }
  }, [location.search]);

  const showThanks = new URLSearchParams(location.search).get("thankyou") === "1" ||
                     new URLSearchParams(location.search).get("success") === "1";

  return (
    <main data-testid="donate-root" className="bg-[#FAFAF7] text-[#0A0612] font-sans">
      <Header />
      <section className="pt-28 pb-10 md:pt-36 md:pb-14 border-b border-[#0A0612]/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C9A227]/50 bg-[#FFFBEC] font-mono text-[10px] tracking-[0.22em] uppercase mb-6">
            <Heart className="h-3 w-3 text-[#C9A227] fill-[#C9A227]" /> Donor Impact Tiers
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6 max-w-4xl">
            Every dollar maps to a <span className="text-[#1B4332]">specific step</span> on a resident&apos;s 730-day path.
          </h1>
          <p className="text-lg md:text-xl text-[#0A0612]/70 max-w-3xl leading-relaxed">
            No general funds. No overhead pool. Pick a tier — we&apos;ll show you exactly which step your gift funds next for someone the world counted last, and email you the receipt within minutes.
          </p>
        </div>
      </section>

      {showThanks && (
        <section className="py-16 md:py-20 border-b border-[#0A0612]/5 bg-white" data-testid="donate-thanks">
          <div className="mx-auto max-w-[700px] px-6 md:px-12 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/10 text-emerald-600 grid place-items-center mb-6">
              <Sparkles className="h-8 w-8" />
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Thank you — deeply.
            </h2>
            <p className="text-[#0A0612]/70 leading-relaxed">
              Your gift just funded a specific step on a specific resident&apos;s path. A receipt is on its way. Watch the /impact page — you&apos;ll see the number move.
            </p>
          </div>
        </section>
      )}

      <section className="py-14 md:py-20" data-testid="donate-tiers">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((t) => (
              <TierCard key={t.id} tier={t} />
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-[#0A0612]/50 max-w-2xl mx-auto leading-relaxed">
            The Exchange Alliance is a 501(c)(3) charitable organization. Every gift is tax-deductible; you&apos;ll receive an official acknowledgment via email.
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
}


/** Named export: compact 3-card strip used inline on the /impact page. */
export function DonorTiersStrip() {
  return (
    <section className="py-14 md:py-16 bg-white border-y border-[#0A0612]/5" data-testid="donor-tiers-strip">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#C9A227] mb-2">Fund a Specific Step</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight max-w-2xl">
              Every dollar earns its keep.
            </h2>
          </div>
          <a
            href="/donate"
            data-testid="tiers-strip-cta"
            className="inline-flex items-center gap-2 border border-[#0A0612] px-5 py-3 font-mono text-[10px] tracking-[0.22em] uppercase hover:bg-[#0A0612] hover:text-[#F4D88A] transition-colors"
          >
            All Ways to Give <ArrowRight className="h-4 w-4" />
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TIERS.map((t) => (
            <TierCard key={t.id} tier={t} compact />
          ))}
        </div>
      </div>
    </section>
  );
}


function TierCard({ tier, compact = false }) {
  const url = tierUrl(tier);
  const Icon = tier.icon;

  const handleClick = async (e) => {
    fireConfetti();
    e.preventDefault();
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/site/donate/checkout`,
        { tier_id: tier.id, origin_url: window.location.origin });
      setTimeout(() => { window.location.href = data.checkout_url; }, 500);
    } catch {
      setTimeout(() => window.alert("Checkout is temporarily unavailable — please try again shortly."), 600);
    }
  };

  return (
    <a
      href={url || "#"}
      onClick={handleClick}
      data-testid={`donate-tier-${tier.id}`}
      className={`group relative flex flex-col rounded-2xl border-2 transition-all overflow-hidden ${
        tier.featured
          ? "border-[#1B4332] bg-white shadow-lg hover:shadow-2xl md:-translate-y-1"
          : "border-[#0A0612]/10 bg-white hover:border-[#C9A227] hover:shadow-xl"
      } ${compact ? "p-6" : "p-8 md:p-10"}`}
      style={{ transitionProperty: "transform, box-shadow, border-color" }}
    >
      {tier.featured && (
        <div className="absolute top-4 right-4 font-mono text-[9px] tracking-[0.22em] uppercase bg-[#1B4332] text-[#F4D88A] px-2.5 py-1">
          Most Impact
        </div>
      )}
      <div className="mb-4 flex items-center gap-3">
        <div className="h-12 w-12 rounded-full grid place-items-center" style={{ background: tier.accent_bg, color: tier.accent }}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0A0612]/50">
            {tier.label}
          </div>
        </div>
      </div>
      <div className={`font-display font-bold tabular-nums tracking-tight text-[#0A0612] ${compact ? "text-4xl" : "text-5xl md:text-6xl"} mb-1`}>
        ${tier.amount}
      </div>
      <div className={`text-[#0A0612]/80 ${compact ? "text-sm" : "text-base md:text-lg"} font-medium mb-3`}>
        {tier.tagline}
      </div>
      {!compact && (
        <div className="text-sm text-[#0A0612]/60 leading-relaxed mb-6 flex-1">
          {tier.detail}
        </div>
      )}
      <div className="mt-auto pt-4 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#0A0612] group-hover:text-[#1B4332] transition-colors">
        Give ${tier.amount} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </div>
    </a>
  );
}
